// /admin/v2/videos/import — the owner's video table → clips (BRIEFS C11.9).
//
// Paste the table (copy the rows from Excel / Google Sheets, header row included) or choose
// a .csv / .xlsx file. 1. Check shows every row (new / already there / mistake) and saves
// nothing. 2. Import makes each good row a video that is waiting for approval (Harsh does
// not see it yet) and starts the AI, which proposes the places in the notes. Then the owner
// approves on "Videos: approve".
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  v2VideoImport, v2VideoImportFile, v2VideoJobs, v2VideoPropose, errText,
} from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';

const muted = { fontSize: 12, color: 'var(--text-muted)' };
const STATUS = {
  new: { label: 'New', color: '#047857', bg: '#D1FAE5' },
  already: { label: 'Already there', color: '#475569', bg: '#F1F5F9' },
  error: { label: 'Mistake', color: '#B91C1C', bg: '#FEF2F2' },
};

function JobBox({ refreshKey }) {
  const { data, refetch } = useQuery({ queryKey: ['v2-video-jobs', refreshKey], queryFn: v2VideoJobs });
  const job = data && data.jobs && data.jobs[0];
  useEffect(() => {
    if (!job || !job.running) return undefined;
    const t = setInterval(() => refetch(), 2000);
    return () => clearInterval(t);
  }, [job, refetch]);
  if (!data) return null;
  if (!data.ai) {
    return (
      <p style={{ ...muted, fontSize: 14, margin: 0 }}>
        The AI key is not set on this server, so no places are proposed. You can still choose
        the places yourself on <Link to="/admin/v2/videos/approve">Videos: approve</Link>.
      </p>
    );
  }
  if (!job) return <p style={{ ...muted, fontSize: 14, margin: 0 }}>No AI run yet.</p>;
  const d = job.details || {};
  return (
    <div data-testid="v2-video-job" style={{ fontSize: 14 }}>
      {job.running
        ? <span>AI is proposing places… {job.done} of {job.total} videos done.</span>
        : <span>
            Last AI run: {job.done} of {job.total} videos done
            {d.saved != null ? ` · ${d.saved} places proposed` : ''}
            {d.no_place ? ` · ${d.no_place} with no place found` : ''}
            {d.failed_calls ? ` · ${d.failed_calls} AI calls failed` : ''}.
          </span>}
      {job.error && <div style={{ color: '#B91C1C', marginTop: 4 }}>{job.error}</div>}
      {!job.running && <div style={{ marginTop: 6 }}><Link to="/admin/v2/videos/approve">Open "Videos: approve" →</Link></div>}
    </div>
  );
}

export default function AdminV2VideoImport() {
  const [mode, setMode] = useState('paste');
  const [textv, setTextv] = useState('');
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [jobKey, setJobKey] = useState(0);

  const ready = mode === 'paste' ? textv.trim().length > 0 : !!file;
  const run = async (dryRun) => {
    setBusy(true); setMsg(null);
    try {
      const out = mode === 'paste' ? await v2VideoImport(textv, dryRun) : await v2VideoImportFile(file, dryRun);
      setReport(out);
      setChecked(dryRun);
      if (!dryRun) {
        const n = out.counts.new;
        setMsg({ kind: 'ok', text: n
          ? `${n} ${n === 1 ? 'video' : 'videos'} imported. They wait for your approval — Harsh does not see them yet.${out.job ? ' The AI is now proposing their places in the notes.' : ''}`
          : 'Nothing new to import.' });
        setJobKey((k) => k + 1);
      }
    } catch (x) {
      setMsg({ kind: 'err', text: errText(x, 'Could not read the table.') });
    } finally { setBusy(false); }
  };
  const proposeAgain = async () => {
    setMsg(null);
    try {
      const out = await v2VideoPropose({});
      setMsg({ kind: 'ok', text: out.job ? `The AI is proposing places for ${out.job.total} videos.` : out.message });
      setJobKey((k) => k + 1);
    } catch (x) { setMsg({ kind: 'err', text: errText(x) }); }
  };
  const changed = () => { setChecked(false); setReport(null); };

  return (
    <div>
      <V2Tabs title="Videos: import your table"
        intro="Paste your video table here (or choose the file). Columns: Topics taught | Description | Video link | Start (mm:ss) | End (mm:ss), and if you like a Chapter column (for example XII-05) and a Kind column (Concept or Question; empty = Concept). Press 1. Check first, then 2. Import. Imported videos wait for your approval; the AI proposes where each one goes in the notes." />
      <Msg msg={msg} />

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button type="button" className={`btn ${mode === 'paste' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setMode('paste'); changed(); }} data-testid="v2-video-mode-paste">Paste the table</button>
          <button type="button" className={`btn ${mode === 'file' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setMode('file'); changed(); }} data-testid="v2-video-mode-file">Use a file (.csv or .xlsx)</button>
        </div>
        {mode === 'paste' ? (
          <label style={{ display: 'block' }}>
            <div style={muted}>Copy the rows in Excel or Google Sheets (with the header row) and paste them here.</div>
            <textarea className="form-input" data-testid="v2-video-text" value={textv}
              onChange={(e) => { setTextv(e.target.value); changed(); }} rows={10}
              placeholder={'Topics taught\tDescription\tVideo link\tStart (mm:ss)\tEnd (mm:ss)\tChapter\tKind\nDNA replication\tMeselson–Stahl experiment\thttps://youtu.be/…\t2:10\t9:45\tXII-05\tConcept'}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }} />
          </label>
        ) : (
          <label style={{ display: 'block' }}>
            <div style={muted}>Choose the file. In Excel, type the times as text (for example 02:30) or save as .csv.</div>
            <input type="file" accept=".csv,.xlsx,.txt" data-testid="v2-video-file"
              onChange={(e) => { setFile(e.target.files[0] || null); changed(); }} />
          </label>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost" disabled={!ready || busy}
            onClick={() => run(true)} data-testid="v2-video-check">1. Check</button>
          <button type="button" className="btn btn-primary"
            disabled={!ready || busy || !checked || !report || report.counts.new === 0}
            onClick={() => run(false)} data-testid="v2-video-import">
            {busy ? 'Working…' : `2. Import${checked && report ? ` ${report.counts.new} new` : ''}`}
          </button>
        </div>
      </div>

      {report && (
        <div className="card" style={{ marginBottom: 20 }} data-testid="v2-video-report">
          <div style={{ marginBottom: 10, fontSize: 15 }}>
            {report.dry_run ? 'Check: ' : 'Imported: '}
            {report.counts.rows} rows · <strong>{report.counts.new} new</strong> · {report.counts.already} already there · {report.counts.errors} with a mistake (skipped)
          </div>
          {report.warnings.map((w) => <div key={w} style={{ color: '#B45309', fontSize: 14, marginBottom: 6 }}>{w}</div>)}
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Row</th><th>Status</th><th>Topics taught</th><th>Video</th><th>Part</th><th>Chapter</th><th>Kind</th></tr></thead>
              <tbody>
                {report.rows.map((r) => {
                  const st = STATUS[r.status];
                  return (
                    <tr key={r.n} data-testid={`v2-video-row-${r.n}`}>
                      <td>{r.n}</td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 12, color: st.color, background: st.bg }}>{st.label}</span>
                        {r.error && <div style={{ color: '#B91C1C', fontSize: 13, marginTop: 4 }}>{r.error}</div>}
                      </td>
                      <td>{r.topics}{r.description && <div style={muted}>{r.description}</div>}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.youtube_id || r.link}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.start}–{r.end}{r.minutes ? ` · ${r.minutes} min` : ''}</td>
                      <td style={{ fontSize: 13 }}>{r.chapter || <span style={muted}>—</span>}</td>
                      <td style={{ fontSize: 13, whiteSpace: 'nowrap' }} data-testid={`v2-video-row-kind-${r.n}`}>
                        {r.kind === 'question' ? 'Question video' : r.kind === 'concept' || !r.kind ? 'Concept video' : r.kind}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0, fontSize: 16 }}>AI proposals</h3>
        <JobBox refreshKey={jobKey} />
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}
          onClick={proposeAgain} data-testid="v2-video-propose">Propose places for videos that have none</button>
      </div>
    </div>
  );
}
